import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { CountUp, Magnetic, Shimmer } from './Motion';
import AnimatedGlobe from './AnimatedGlobe';

const CorporateHero = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-white"
      data-testid="corporate-hero"
    >
      {/* Thin black accent strip across the top — luxury detail */}
      <div className="absolute top-0 left-0 right-0 h-10 bg-[#0a0a0a] z-30 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-8 flex items-center justify-between">
          <span className="text-white/40 text-[10px] tracking-[0.3em] uppercase">Est. 2010</span>
          <span className="hidden md:inline text-[#C9A227] text-[10px] tracking-[0.3em] uppercase">Fort Lauderdale · Nassau · London</span>
        </div>
      </div>

      {/* Subtle gold radial glow */}
      <motion.div
        className="absolute top-1/3 right-[10%] w-[500px] h-[500px] rounded-full opacity-[0.08] blur-3xl"
        style={{ background: 'radial-gradient(circle, #C9A227 0%, transparent 70%)' }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Faint dot grid */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0a0a0a 1px, transparent 0)',
        backgroundSize: '40px 40px',
      }} />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="inline-flex items-center gap-3 mb-8"
          >
            <div className="h-[1px] w-8 bg-[#C9A227]" />
            <AnimatedGlobe size={18} className="!align-middle" />
            <span className="text-[#C9A227] text-[10px] tracking-[0.4em] uppercase font-semibold">Corporate Technology Group</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="text-[#0a0a0a] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-6 tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            data-testid="corporate-hero-title"
          >
            Sebastian Alliance Group
            <br />
            <span className="text-[#C9A227] italic">Digital Infrastructure</span>
            <br />
            <span className="text-[#0a0a0a]/75">for Commerce, Finance,</span>
            <br />
            <span className="text-[#0a0a0a]/75">Payments &amp; Innovation</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="text-gray-500 text-base md:text-lg leading-relaxed max-w-2xl mb-10 font-light"
          >
            A corporate technology group building secure digital platforms for businesses,
            financial institutions, merchants, hotels, real estate, and emerging markets.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Magnetic strength={0.35}>
              <a
                href="#ecosystem-visual"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#0a0a0a] text-white text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#C9A227] hover:text-[#0a0a0a] transition-all hover:-translate-y-0.5 hover:shadow-2xl relative overflow-hidden"
                data-testid="hero-explore-group-btn"
              >
                <Shimmer>
                  <span className="relative z-10">Explore Our Group</span>
                </Shimmer>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform relative z-10" />
              </a>
            </Magnetic>
            <Magnetic strength={0.3}>
              <a
                href="#cta-consult"
                className="group inline-flex items-center gap-3 px-8 py-4 border border-[#C9A227] text-[#C9A227] text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#C9A227] hover:text-[#0a0a0a] transition-all"
                data-testid="hero-consult-btn"
              >
                Schedule a Consultation
              </a>
            </Magnetic>
          </motion.div>

          {/* Stat strip — animated count-up */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="mt-16 grid grid-cols-3 gap-6 max-w-2xl border-t border-gray-200 pt-8"
          >
            {[
              { value: '6+', label: 'Digital Platforms' },
              { value: '3', label: 'Global Offices' },
              { value: '15+', label: 'Years of Expertise' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-[#C9A227] text-2xl md:text-3xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  <CountUp value={s.value} duration={2 + i * 0.3} />
                </div>
                <div className="text-gray-400 text-[10px] tracking-[0.2em] uppercase mt-1">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right side — Corporate dot-network globe */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.4 }}
          className="lg:col-span-5 hidden lg:flex items-center justify-center relative"
          style={{ perspective: '1000px' }}
        >
          <AnimatedGlobe size={440} />
        </motion.div>
      </div>

      {/* Gold bottom line */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
    </section>
  );
};

export default CorporateHero;
